import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList, ListRenderItem, Platform } from 'react-native';
import { showConfirm } from '@/utils/common/alert';
import { StackScreenProps } from '@react-navigation/stack';
import { 
  BaseAdminScreen, 
  AdminEmptyScreenLayout, 
  LoadingSpinner,
  TaskRow,
  QuickAddTask,
  TableFilters,
  MonthYearPicker,
} from '@/components';
import AdminErrorState from '@/components/common/admin/mobile/AdminErrorState';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useTasks } from '@/hooks/admin/useTasks';
import { ITaskItemAttributes } from '@/types/admin/tasks';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { TaskFiltersErrorBoundary } from '@/components/admin/tasks/TaskFiltersErrorBoundary';
import { useErrorToast, useToast } from '@/contexts/admin/ToastContext';
import { useDuplicateTaskMutation, useArchiveTaskMutation } from '@/store/api/admin/tasksApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ADMIN_COLORS, EMPTY_MESSAGES } from '@/constants/admin';
import { InlineEmptyMessage } from '@/components';
import { useErrorToastOnce } from '@/hooks/common/useErrorToastOnce';
import { TASK_VIEW_TYPES, TASK_STATUS_CONFIG } from '@/types/admin/tasks';
import { TASK_STATUSES } from '@/constants/admin/taskConstants';
import { IFilterModel, IColumnConfig, ISortModel } from '@/types/admin/dataTable';
import { MonthYearPickerErrorBoundary } from '@/components/common/admin/mobile/MonthYearPickerErrorBoundary';
import { formatMonthYear } from '@/utils/admin/date';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopTasksView } from '@/components/admin/tasks/desktop/DesktopTasksView';
import { TasksAccordionList } from '@/components/admin/tasks/mobile/TasksAccordionList';
import { TasksFiltersHeader } from '@/components/admin/tasks/mobile/TasksFiltersHeader';
import { toDomainTaskFilterModel, toGenericTaskFilterModel, buildTaskTableColumns, hasActiveTaskFilters } from '@/utils/admin/tasks';

type TasksScreenProps = StackScreenProps<AdminStackParamList, 'Tasks'>;

export const TasksScreen: React.FC<TasksScreenProps> = ({
  navigation,
}) => {
  const { layoutMode } = useResponsive();
  const [viewMode, setViewMode] = useState<'accordion' | 'table'>('accordion');
  const [forceOpenIncrement, setForceOpenIncrement] = useState(0);
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);
  const { showApiError } = useErrorToast();
  const { showSuccess } = useToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  
  // Task mutations
  const [duplicateTask, { isLoading: isDuplicating }] = useDuplicateTaskMutation();
  const [archiveTask, { isLoading: isArchiving }] = useArchiveTaskMutation();

  const {
    data,
    visibleTasks,
    tableRows,
    groupedTasks,
    isLoading,
    enhancedError,
    retryCount,
    canRetry,
    manualRetry,
    view,
    searchQuery,
    filterModel,
    sortModel,
    uniqueAssignees,
    updateView,
    updateSearchQuery,
    updateFilterModel,
    updateSortModel,
    refresh,
    handleQuickCreateTask,
    quickAddInputs,
    updateQuickAddInput,
    clearQuickAddInput,
    statusConfig,
  } = useTasks();

  // Show toast for API errors (guard duplicates)
  useErrorToastOnce(enhancedError, showApiError);

  // Force open accordions when filters are active
  const hasActiveFilters = hasActiveTaskFilters(filterModel as any, searchQuery, view as any);

  useEffect(() => {
    if (hasActiveFilters) {
      setForceOpenIncrement(prev => prev + 1);
    } else {
      setForceOpenIncrement(0);
    }
  }, [hasActiveFilters]);

  // Columns for TableFilters (filters + sort options)
  const getTaskFilterColumns = (): IColumnConfig<any>[] => buildTaskTableColumns(view, uniqueAssignees);

  // Adapters between domain and generic filter models
  const toGenericFilterModel = (f: any): IFilterModel => toGenericTaskFilterModel(f);
  const toDomainFilterModel = (m: IFilterModel, prev: any): any => toDomainTaskFilterModel(m, prev as any);

  const monthYearLabel = () => formatMonthYear(filterModel.month, filterModel.year);

  const monthYearHeaderRight = (
    view === TASK_VIEW_TYPES.ARCHIVED ? (
      <TouchableOpacity
        style={styles.monthYearButton}
        onPress={() => setShowMonthYearPicker(true)}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <SVG_COMPONENTS.CALENDAR width={16} height={16} />
        <Text style={styles.monthYearButtonText}>{monthYearLabel()}</Text>
      </TouchableOpacity>
    ) : null
  );

  const renderViewTabs = () => (
    <View style={styles.viewTabs}>
      <TouchableOpacity
        style={[styles.viewTab, view === TASK_VIEW_TYPES.LIST && styles.activeViewTab]}
        onPress={() => updateView(TASK_VIEW_TYPES.LIST)}
      >
        <Text style={[styles.viewTabText, view === TASK_VIEW_TYPES.LIST && styles.activeViewTabText]}>
          Tasks
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.viewTab, view === TASK_VIEW_TYPES.ARCHIVED && styles.activeViewTab]}
        onPress={() => updateView(TASK_VIEW_TYPES.ARCHIVED)}
      >
        <Text style={[styles.viewTabText, view === TASK_VIEW_TYPES.ARCHIVED && styles.activeViewTabText]}>
          Archived
        </Text>
      </TouchableOpacity>
    </View>
  );



  const onShowDetails = (taskId: number) => {
    navigation.navigate('EditTask', { taskId });
  };

  const onEditTask = (taskId: number) => {
    navigation.navigate('EditTask', { taskId });
  };

  const onDuplicateTask = useCallback(async (taskId: number) => {
    // Find the original task from the current data
    const originalTask = visibleTasks.find(task => task.id === taskId);
    
    if (!originalTask) {
      showApiError('Task not found');
      return;
    }

    if (!selectedPropertyId) {
      showApiError('No property selected');
      return;
    }

    // On web, RN Alert doesn't support multi-button confirm. Execute immediately.
    if (Platform.OS === 'web') {
      try {
        await duplicateTask({ taskId, originalTask, selectedPropertyId }).unwrap();
        showSuccess('Task duplicated successfully!');
      } catch (error: any) {
        console.error('Failed to duplicate task:', error);
        showApiError(error?.data?.message || error?.error || 'Failed to duplicate task');
      }
      return;
    }

    const confirmed = await showConfirm(
      'Duplicate Task',
      'Are you sure you want to duplicate this task?',
      'Duplicate',
      'Cancel'
    );
    
    if (!confirmed) return;
    
    try {
      await duplicateTask({ taskId, originalTask, selectedPropertyId }).unwrap();
      showSuccess('Task duplicated successfully!');
    } catch (error: any) {
      console.error('Failed to duplicate task:', error);
      showApiError(error?.data?.message || error?.error || 'Failed to duplicate task');
    }
  }, [duplicateTask, showSuccess, showApiError, visibleTasks, selectedPropertyId]);

  const onArchiveTask = useCallback(async (taskId: number) => {
    const isCurrentlyArchived = view === TASK_VIEW_TYPES.ARCHIVED;
    const actionText = isCurrentlyArchived ? 'unarchive' : 'archive';
    const actionTitle = isCurrentlyArchived ? 'Unarchive Task' : 'Archive Task';
    
    // On web, RN Alert doesn't support multi-button confirm. Execute immediately.
    if (Platform.OS === 'web') {
      try {
        await archiveTask({ 
          taskId, 
          isArchived: !isCurrentlyArchived,
          archivedAt: !isCurrentlyArchived ? new Date().toISOString() : null
        }).unwrap();
        showSuccess(`Task ${actionText}d successfully!`);
      } catch (error: any) {
        console.error(`Failed to ${actionText} task:`, error);
        showApiError(error?.data?.message || `Failed to ${actionText} task`);
      }
      return;
    }

    const confirmed = await showConfirm(
      actionTitle,
      `Are you sure you want to ${actionText} this task?`,
      actionTitle.split(' ')[0],
      'Cancel'
    );
    
    if (!confirmed) return;
    
    try {
      await archiveTask({ 
        taskId, 
        isArchived: !isCurrentlyArchived,
        archivedAt: !isCurrentlyArchived ? new Date().toISOString() : null
      }).unwrap();
      showSuccess(`Task ${actionText}d successfully!`);
    } catch (error: any) {
      console.error(`Failed to ${actionText} task:`, error);
      showApiError(error?.data?.message || `Failed to ${actionText} task`);
    }
  }, [archiveTask, showSuccess, showApiError, view]);

  const keyExtractor = (item: ITaskItemAttributes) => String(item.id);

  const renderTaskItem: ListRenderItem<ITaskItemAttributes> = ({ item }) => (
    <TaskRow
      task={item}
      onShowDetails={onShowDetails}
      onEdit={onEditTask}
      onDuplicate={isDuplicating ? undefined : onDuplicateTask}
      onArchive={isArchiving ? undefined : onArchiveTask}
      isArchived={view === TASK_VIEW_TYPES.ARCHIVED}
    />
  );

  const renderAccordionView = () => {
    const safeGroupedTasks = groupedTasks || {};

    const sections = [
      {
        key: 'incoming',
        status: TASK_STATUSES.INCOMING,
        icon: <Text style={styles.emoji}>🏠</Text>,
        color: undefined as string | undefined,
        quickAdd: null as React.ReactNode,
      },
      {
        key: 'pending',
        status: TASK_STATUSES.PENDING,
        icon: null as React.ReactNode,
        color: ADMIN_COLORS.TEXT_SECONDARY,
        quickAdd: (
          <QuickAddTask
            taskStatus={TASK_STATUSES.PENDING}
            value={quickAddInputs.pending || ''}
            onValueChange={handleQuickAddPending}
            onSubmit={handleQuickCreateTask}
            onClear={handleClearPending}
          />
        ),
      },
      {
        key: 'todo',
        status: TASK_STATUSES.TODO,
        icon: null as React.ReactNode,
        color: '#3B82F6',
        quickAdd: (
          <QuickAddTask
            taskStatus={TASK_STATUSES.TODO}
            value={quickAddInputs.todo || ''}
            onValueChange={handleQuickAddTodo}
            onSubmit={handleQuickCreateTask}
            onClear={handleClearTodo}
          />
        ),
      },
      {
        key: 'inProgress',
        status: TASK_STATUSES.IN_PROGRESS,
        icon: null as React.ReactNode,
        color: '#F59E0B',
        quickAdd: (
          <QuickAddTask
            taskStatus={TASK_STATUSES.IN_PROGRESS}
            value={quickAddInputs.inProgress || ''}
            onValueChange={handleQuickAddInProgress}
            onSubmit={handleQuickCreateTask}
            onClear={handleClearInProgress}
          />
        ),
      },
      {
        key: 'completed',
        status: TASK_STATUSES.COMPLETED,
        icon: null as React.ReactNode,
        color: '#10B981',
        quickAdd: (
          <QuickAddTask
            taskStatus={TASK_STATUSES.COMPLETED}
            value={quickAddInputs.completed || ''}
            onValueChange={handleQuickAddCompleted}
            onSubmit={handleQuickCreateTask}
            onClear={handleClearCompleted}
          />
        ),
      },
      {
        key: 'cancelled',
        status: TASK_STATUSES.CANCELLED,
        icon: (
          <SVG_COMPONENTS.X_CIRCLE
            width={16}
            height={16}
            fill="#EF4444"
          />
        ),
        color: '#EF4444',
        quickAdd: null as React.ReactNode,
      },
    ] as const;

    const listHeader = (
      <View style={styles.listHeaderContainer}>
        <TasksFiltersHeader
          view={view as any}
          onChangeView={(v) => updateView(v as any)}
          columns={getTaskFilterColumns()}
          searchQuery={searchQuery}
          onSearchChange={updateSearchQuery}
          filterModel={toGenericFilterModel(filterModel)}
          onFilterChange={(m: IFilterModel) => updateFilterModel(toDomainFilterModel(m, filterModel))}
          sortModel={sortModel as unknown as ISortModel[]}
          onSortChange={(s: ISortModel[]) => updateSortModel(s as any)}
          headerRight={monthYearHeaderRight}
          onRetry={refresh}
        />
      </View>
    );

    return (
      <View style={{ flex: 1 }}>
        {listHeader}
        <TasksAccordionList
          sections={sections.map(s => ({ key: s.key, label: TASK_STATUS_CONFIG[s.status].label, color: s.color, icon: s.icon, quickAdd: s.quickAdd }))}
          groupedTasks={safeGroupedTasks as any}
          forceOpenIncrement={forceOpenIncrement}
          hasActiveFilters={hasActiveFilters}
          isArchived={view === TASK_VIEW_TYPES.ARCHIVED}
          onShowDetails={onShowDetails}
          onEdit={onEditTask}
          onDuplicate={isDuplicating ? undefined : onDuplicateTask}
          onArchive={isArchiving ? undefined : onArchiveTask}
        />
      </View>
    );
  };

  // Render archived tasks as a simple list (like admin-web)
  const renderArchivedTasksList = () => {
    const archivedTasks = visibleTasks || [];
    
    return (
      <FlatList
        style={styles.archivedTasksList}
        data={archivedTasks}
        ListHeaderComponent={(
          <View style={styles.listHeaderContainer}>
            {renderViewTabs()}
            <TaskFiltersErrorBoundary onRetry={refresh}>
              <TableFilters
                columns={getTaskFilterColumns()}
                searchQuery={searchQuery}
                onSearchChange={updateSearchQuery}
                filterModel={toGenericFilterModel(filterModel)}
                onFilterChange={(m: IFilterModel) => updateFilterModel(toDomainFilterModel(m, filterModel))}
                sortModel={sortModel as unknown as ISortModel[]}
                onSortChange={(s: ISortModel[]) => updateSortModel(s as any)}
                headerRight={monthYearHeaderRight}
              />
            </TaskFiltersErrorBoundary>
          </View>
        )}
        renderItem={({ item }) => (
          <TaskRow
            task={item}
            onShowDetails={onShowDetails}
            onEdit={onEditTask}
            onDuplicate={isDuplicating ? undefined : onDuplicateTask}
            onArchive={isArchiving ? undefined : onArchiveTask}
            isArchived={true}
          />
        )}
        keyExtractor={keyExtractor}
        initialNumToRender={12}
        maxToRenderPerBatch={12}
        windowSize={7}
        updateCellsBatchingPeriod={50}
        removeClippedSubviews
        showsVerticalScrollIndicator={false}
      />
    );
  };

  const renderEmptyMessage = () => {
    const monthYear = (filterModel.month && filterModel.year)
      ? new Date(filterModel.year, filterModel.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })
      : undefined;

    const cfg = view === TASK_VIEW_TYPES.ARCHIVED
      ? (monthYear
          ? EMPTY_MESSAGES.tasks.archivedForMonth(monthYear)
          : hasActiveFilters
            ? EMPTY_MESSAGES.tasks.archivedWithFilters
            : EMPTY_MESSAGES.tasks.archivedDefault)
      : (hasActiveFilters
          ? EMPTY_MESSAGES.tasks.withFilters
          : EMPTY_MESSAGES.tasks.default);

    return (
      <InlineEmptyMessage
        title={cfg.title}
        message={cfg.message}
        icon={cfg.icon}
        containerStyle={styles.emptyMessageContainer}
        titleStyle={styles.emptyMessageText}
        messageStyle={styles.emptyMessageSubtext}
      />
    );
  };

  // Handle create task functionality
  const handleCreateTask = useCallback(() => {
    navigation.navigate('CreateTask');
  }, [navigation]);

  // Optimized handlers for QuickAddTask components
  const handleQuickAddPending = useCallback((value: string) => 
    updateQuickAddInput(TASK_STATUSES.PENDING, value), [updateQuickAddInput]);
  
  const handleClearPending = useCallback(() => 
    clearQuickAddInput(TASK_STATUSES.PENDING), [clearQuickAddInput]);

  const handleQuickAddTodo = useCallback((value: string) => 
    updateQuickAddInput(TASK_STATUSES.TODO, value), [updateQuickAddInput]);
  
  const handleClearTodo = useCallback(() => 
    clearQuickAddInput(TASK_STATUSES.TODO), [clearQuickAddInput]);

  const handleQuickAddInProgress = useCallback((value: string) => 
    updateQuickAddInput(TASK_STATUSES.IN_PROGRESS, value), [updateQuickAddInput]);
  
  const handleClearInProgress = useCallback(() => 
    clearQuickAddInput(TASK_STATUSES.IN_PROGRESS), [clearQuickAddInput]);

  const handleQuickAddCompleted = useCallback((value: string) => 
    updateQuickAddInput(TASK_STATUSES.COMPLETED, value), [updateQuickAddInput]);
  
  const handleClearCompleted = useCallback(() => 
    clearQuickAddInput(TASK_STATUSES.COMPLETED), [clearQuickAddInput]);

  // Determine if we should show data or empty state
  const hasData = Array.isArray(visibleTasks) && visibleTasks.length > 0;
  
  // Show error state if we have a critical error and no data
  const showErrorState = enhancedError && !hasData && !isLoading;
  
  // Show loading state if we're loading and have no data yet
  const showLoadingState = isLoading && !hasData && !showErrorState;

  // Render error state component
  const renderErrorState = () => (
    <AdminErrorState
      title="Unable to Load Tasks"
      message={enhancedError?.userFriendlyMessage || 'Something went wrong while loading the tasks.'}
      retryCount={retryCount}
      canRetry={canRetry}
      onRetry={manualRetry}
    />
  );

  // Render content based on view mode
  const renderContent = () => {
    if (showLoadingState) {
      return <LoadingSpinner message="Loading tasks..." />;
    }
    
    if (showErrorState) {
      return renderErrorState();
    }

    if (viewMode === 'accordion') {
      return (
        <View style={styles.accordionViewContainer}>
          {view === TASK_VIEW_TYPES.ARCHIVED ? (
            hasData ? renderArchivedTasksList() : (
              // Render list with just header so filters can scroll even with no items
              <FlatList
                style={styles.archivedTasksList}
                data={[]}
                ListHeaderComponent={(
                  <View style={styles.listHeaderContainer}>
                    {renderViewTabs()}
                    <TaskFiltersErrorBoundary onRetry={refresh}>
                      <TableFilters
                        columns={getTaskFilterColumns()}
                        searchQuery={searchQuery}
                        onSearchChange={updateSearchQuery}
                        filterModel={toGenericFilterModel(filterModel)}
                        onFilterChange={(m: IFilterModel) => updateFilterModel(toDomainFilterModel(m, filterModel))}
                        sortModel={sortModel as unknown as ISortModel[]}
                        onSortChange={(s: ISortModel[]) => updateSortModel(s as any)}
                        headerRight={monthYearHeaderRight}
                      />
                    </TaskFiltersErrorBoundary>
                    {renderEmptyMessage()}
                  </View>
                )}
                keyExtractor={() => 'header-only'}
                renderItem={() => null}
              />
            )
          ) : (
            // Always show accordion for non-archived view, even when there are no matches
            renderAccordionView()
          )}
        </View>
      );
    }

    // Table view for larger screens
    return (
      <View style={styles.tableViewContainer}>
        {renderViewTabs()}
        
        <TaskFiltersErrorBoundary onRetry={refresh}>
          <TableFilters
            columns={getTaskFilterColumns()}
            searchQuery={searchQuery}
            onSearchChange={updateSearchQuery}
            filterModel={toGenericFilterModel(filterModel)}
            onFilterChange={(m: IFilterModel) => updateFilterModel(toDomainFilterModel(m, filterModel))}
            sortModel={sortModel as unknown as ISortModel[]}
            onSortChange={(s: ISortModel[]) => updateSortModel(s as any)}
            headerRight={monthYearHeaderRight}
          />
        </TaskFiltersErrorBoundary>

        {hasData ? (
          <View style={styles.tableContainer}>
            <Text style={styles.tableText}>Table view coming soon...</Text>
          </View>
        ) : (
          renderEmptyMessage()
        )}
      </View>
    );
  };

  // Desktop rendering mirrors admin-web
  if (layoutMode === 'desktop') {
    const tasks = useMemo(() => visibleTasks || [], [visibleTasks]);
    return (
      <AdminErrorBoundary context="TasksScreenDesktop">
        <DesktopTasksView
          view={view as any}
          tasks={tasks}
          groupedTasks={groupedTasks as any}
          searchQuery={searchQuery}
          onSearchChange={updateSearchQuery}
          filterModel={filterModel}
          onFilterChange={updateFilterModel}
          sortModel={sortModel as any}
          onSortChange={(s) => updateSortModel(s as any)}
          uniqueAssignees={uniqueAssignees}
          onCreateTask={() => navigation.navigate('CreateTask')}
          onChangeView={(v) => updateView(v as any)}
          onEditTask={(id) => navigation.navigate('EditTask', { taskId: id })}
          onDuplicateTask={isDuplicating ? undefined : onDuplicateTask}
          onArchiveTask={isArchiving ? undefined : onArchiveTask}
          quickAddInputs={quickAddInputs}
          handleQuickCreateTask={handleQuickCreateTask}
          updateQuickAddInput={updateQuickAddInput}
          clearQuickAddInput={clearQuickAddInput}
          headerRight={monthYearHeaderRight}
        />
        {/* Month/Year Picker Modal (desktop as well) */}
        <MonthYearPickerErrorBoundary 
          visible={showMonthYearPicker}
          onClose={() => setShowMonthYearPicker(false)}
        >
          <MonthYearPicker
            visible={showMonthYearPicker}
            onClose={() => setShowMonthYearPicker(false)}
            onSelect={(month: number, year: number) => updateFilterModel({ ...filterModel, month, year })}
            selectedMonth={filterModel.month}
            selectedYear={filterModel.year}
            webAnchor={undefined}
          />
        </MonthYearPickerErrorBoundary>
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary context="TasksScreen">
      <BaseAdminScreen
        screenName="Tasks"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <AdminEmptyScreenLayout
          title="Tasks"
          emptyStateType="residents"
          onAddPress={handleCreateTask}
          showAddButton={true}
          buttonType="add"
          hasContent={true} // Always show content since we handle empty states internally
          disableScrollView={true} // Keep wrapper stable to avoid input blur when content toggles
        >
          {renderContent()}

          {/* Month/Year Picker Modal */}
          <MonthYearPickerErrorBoundary 
            visible={showMonthYearPicker}
            onClose={() => setShowMonthYearPicker(false)}
          >
            <MonthYearPicker
              visible={showMonthYearPicker}
              onClose={() => setShowMonthYearPicker(false)}
              onSelect={(month: number, year: number) => updateFilterModel({ ...filterModel, month, year })}
              selectedMonth={filterModel.month}
              selectedYear={filterModel.year}
            />
          </MonthYearPickerErrorBoundary>
        </AdminEmptyScreenLayout>
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  accordionViewContainer: {
    flex: 1,
  },
  tableViewContainer: {
    flex: 1,
    padding: 16,
  },
  viewTabs: {
    flexDirection: 'row',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    padding: 4,
    marginBottom: 16,
  },
  viewTab: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
  },
  activeViewTab: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  viewTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  activeViewTabText: {
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },

  accordionContainer: {
    flex: 1,
  },
  archivedTasksList: {
    flex: 1,
  },
  accordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  emoji: {
    fontSize: 18,
  },
  statusDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '300',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  headerCount: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  taskList: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
  },
  emptySection: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
  },
  
  // Error state styles removed (using shared AdminErrorState)

  // Empty message styles (when tabs are visible but no data)
  emptyMessageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
  },
  emptyMessageIcon: {
    marginBottom: 16,
  },
  emptyMessageText: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyMessageSubtext: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 300, // Limit width for better readability like admin-web
  },

  // Table view styles
  tableContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  tableText: {
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontSize: 16,
  },
  monthYearButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  monthYearButtonText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontWeight: '500',
  },
  listHeaderContainer: {
    marginBottom: 12,
  },
});